import { Alert, AlertIcon, Code, Text } from "@chakra-ui/react";

import SecureDatasetsContent from "@/components/Datasets/SecureDatasetsContent";
import DocLink from "@/components/shared/DocLink";
import ExternalLink from "@/components/shared/ExternalLink";
import uuid from "@/utils/uuid";

export const learnMore = {
  buildArguments: {
    heading: "Build arguments",
    paragraphs: [
      {
        paragraph:
          "Build arguments are key:value pairs that will be used to initialize a build with either docker-compose or static service environment variables. They are parameters passed into your builds before containers are created. They are generally used as arguments to a Docker build.",
        key: uuid(),
      },
    ],
  },
  cloneApp: [
    {
      heading: "Cloning an App",
      paragraphs: [
        {
          paragraph: (
            <Text>
              Cloning an app will make a copy of the selected app configuration with a new name.
              <Alert key="alert" status="info">
                <AlertIcon />
                No environments are copied over.
              </Alert>
            </Text>
          ),
          key: uuid(),
        },
        {
          paragraph: (
            <Text>
              If development environments are enabled, opening or labeling a pull request can automatically
              generate an environment. Otherwise environments will need to be manually created if development
              environments are disabled.
            </Text>
          ),
          key: uuid(),
        },
      ],
      key: "cloneApp",
    },
  ],
  createApp: [
    {
      heading: "Can’t see your repository?",
      paragraphs: [
        {
          paragraph: (
            <Text>
              Ensure that you&apos;ve correctly configured and connected a supported version control system
              and have given Release permissions to see your repositories.
            </Text>
          ),
          key: uuid(),
        },
      ],
      key: "cantSeeRepo",
    },
  ],
  "createApp.selectServices": {
    heading: "Picking your services",
    paragraphs: [
      {
        paragraph: (
          <Text>
            When you have selected a repository, Release will analyze the specified branch on that repository
            to find all the services it contains. This information is gathered from your{" "}
            <Code>docker-compose.yml</Code> and <Code>package.json</Code> files.
          </Text>
        ),
        key: uuid(),
      },
      {
        paragraph: (
          <Text>
            Review the detected services to ensure they are correct and configured how you wish. If there is a
            service missing, review your <Code>docker-compose</Code> file.
          </Text>
        ),
        key: uuid(),
      },
    ],
  },
  "createApp.generateTemplate": {
    heading: "Overview",
    paragraphs: [
      {
        paragraph:
          "Release automatically generates an Application Template from your repository, branch, and service configuration for this application. The Application Template is a configuration template that combines directives derived from your configuration files and some defaults generated by Release.",
        key: uuid(),
      },
      {
        paragraph:
          "The Application Template consists of templates for your permanent and ephemeral environments, resources allocated for each service and environment, the services and jobs needed to run your application, and workflows to set up your environments and deploy new code.",
        key: uuid(),
      },
    ],
  },
  "createApp.saveAndFinish": {
    heading: "Save & Finish",
    paragraphs: [
      {
        paragraph:
          "Release will finish creating your Application and you'll be ready to create your first Environment.",
        key: uuid(),
      },
    ],
  },
  createSshKey: {
    heading: "Using SSH Keys during Builds",
    paragraphs: [
      {
        paragraph:
          "Release allows you to add your SSH Keys and have them securely passed into your Builds so that private repositories can be cloned.",
        key: uuid(),
      },
      {
        paragraph: (
          <>
            <Text>You can read the Docker Documentation below</Text>
            <ExternalLink
              href="https://docs.docker.com/build/building/secrets/#ssh-mounts"
              text="SSH mounts"
            />
            <ExternalLink
              href="https://docs.docker.com/reference/cli/docker/buildx/build/#ssh"
              text="SSH agent socket or keys to expose to the build (--ssh)"
            />
          </>
        ),
        key: uuid(),
      },
    ],
  },
  environmentVariables: {
    heading: "Environment variables",
    paragraphs: [
      {
        paragraph:
          "You can set default environment variables that will be available for all environments for your application. These environment variables consist of a key, a value, and an optional secret flag.",
        key: "environment-variables",
      },
      {
        paragraph: (
          <Text>
            You can read the full documentation <DocLink route="defaultEnvVars" text="here" />
            <DocLink route="defaultEnvVars" text="here" />
          </Text>
        ),
        key: uuid(),
      },
      {
        paragraph: (
          <Text>
            The environment variables template has three top level keys, <Code>defaults</Code>,{" "}
            <Code>services</Code>, and <Code>mapping</Code>. We&apos;ll cover the syntax for each here as they
            all slightly vary.
          </Text>
        ),
        key: uuid(),
      },
      {
        paragraph: (
          <>
            <Text>
              <Code>defaults</Code> are the environment variables that are applied to all services. The syntax
              is an array of objects with <Code>key</Code>, <Code>value</Code>, and optionally{" "}
              <Code>secret</Code> attributes.
            </Text>
            <br />
            <Code whiteSpace="pre">
              defaults:
              <br />
              - key: FIRST_KEY
              <br />
              &nbsp;&nbsp;value: FIRST_VALUE
              <br />
              - key: FIRST_SECRET_KEY
              <br />
              &nbsp;&nbsp;value: FIRST_SECRET_VALUE
              <br />
              &nbsp;&nbsp;secret: true
            </Code>
          </>
        ),
        key: uuid(),
      },
      {
        paragraph: (
          <>
            <Text>
              <Code>services</Code> are the environment variables that are applied to only one service. If you
              need to override a default value, keys in the <Code>services</Code> take precedence over ones in{" "}
              <Code>defaults</Code>. The syntax is a key with the service name and then an array of objects
              with
              <Code>key</Code>, <Code>value</Code>, and optionally <Code>secret</Code> attributes.
            </Text>
            <br />
            <Code whiteSpace="pre">
              services:
              <br />
              &nbsp;&nbsp;api:
              <br />
              &nbsp;&nbsp;- key: FIRST_SERVICE_KEY
              <br />
              &nbsp;&nbsp;&nbsp;&nbsp;value: FIRST_SERVICE_VALUE
              <br />
              &nbsp;&nbsp;- key: FIRST_SERVICE_SECRET_KEY
              <br />
              &nbsp;&nbsp;&nbsp;&nbsp;value: FIRST_SERVICE_SECRET_VALUE
              <br />
              &nbsp;&nbsp;&nbsp;&nbsp;secret: true
            </Code>
          </>
        ),
        key: uuid(),
      },
      {
        paragraph: (
          <>
            <Text>
              <Code>mapping</Code> is the ability to map the value from one environment variable key and to
              another. The syntax differs from <Code>defaults</Code> and <Code>services</Code> and is a hash
              with the key to map to on the left and the key to map from on the right.
              <br />
              Common uses for mapping are taking the environment variables Release generates for URLs and
              mapping them to the environment variables your application expects or doing a concatenation of
              values.
            </Text>
            <br />
            <Text>
              You can read the full mapping documentation <DocLink route="envVarMappings" text="here" />
            </Text>
            <br />
            <Code>
              mapping:
              <br />
              KEY_TO_MAP_TO: KEY_TO_MAP_FROM
              <br />
              NEXT_PUBLIC_BACKEND_URL: BACKEND_INGRESS_URL
              <br />
              CUSTOM_HOSTNAME: {'"${RELEASE_ENV_ID}.custom.release.com"'}
            </Code>
          </>
        ),
        key: uuid(),
      },
    ],
  },
  applicationTemplate: {
    heading: "Application Template",
    paragraphs: [
      {
        paragraph: (
          <>
            <Text>
              The application template is used to build your environments. It defines the globally used domain
              names, services, resources and workflows.
            </Text>
            <DocLink route="defaultEnvConfig" text="Learn more about Application Templates" />
          </>
        ),
        key: uuid(),
      },
    ],
  },
  cloudIntegrations: {
    heading: "Cloud Integrations",
    paragraphs: [
      {
        paragraph: (
          <>
            <Text mb={4}>
              Cloud Integrations are how your Clusters can be managed inside of your chosen Cloud Provider
              account.
            </Text>
            <DocLink route="awsIntegration" text="Learn more about Cloud Integrations" />
          </>
        ),
        key: uuid(),
      },
    ],
  },
  clusters: {
    heading: "Clusters",
    paragraphs: [
      {
        paragraph: (
          <>
            <Text mb={4}>Environments created on Release are deployed onto Kubernetes clusters.</Text>
            <DocLink route="createClusters" text="Learn more about creating Clusters" />
          </>
        ),
        key: uuid(),
      },
    ],
  },
  "cloudIntegrations.aws": {
    heading: "AWS",
    paragraphs: [
      {
        paragraph:
          "After choosing the Integration name and selecting Amazon as your Cloud Provider, you will need to successfully launch a CloudFormation stack. This will create the required IAM role in your AWS account to allow Release to manage the datasets on your behalf in AWS. Please make sure to set up the Cloud Integration in the AWS account where your snapshots are located.",
        key: uuid(),
      },
    ],
  },
  datasets: {
    heading: "Datasets",
    paragraphs: [
      {
        paragraph: (
          <>
            <Text mb={4}>
              Datasets are a collection of databases that are available and ready to be checked out by
              environments. These databases are based on production snapshots available in your connected
              cloud integration. Datasets are configured to a certain size to ensure exactly that many
              databases are available at any given time. Each time a database is checked out, your dataset
              will spin up a replacement so that you never run out or wait.
            </Text>
            <DocLink route="datasetsAws" text="Learn more about Datasets" />
          </>
        ),
        key: uuid(),
      },
    ],
  },
  secrets: {
    heading: "Secrets",
    paragraphs: [
      {
        paragraph: (
          <>
            <Text mb={4}>
              Secrets are a collection of key/value pairs that are encrypted and used for sensitive
              information. You can manage secrets through our provider which works across cloud integrations
              or you may reference secrets which you have created and managed from your configured cloud
              integration in AWS or GCP.
            </Text>
            <DocLink route="managingSecrets" text="Learn more about Secrets" />
          </>
        ),
        key: uuid(),
      },
    ],
  },
  members: {
    heading: "Members",
    paragraphs: [
      {
        paragraph: (
          <>
            <Text mb={4}>
              Members are users associated to the current Workspace. You can invite others to join your
              Workspace or modify their permissions here.
            </Text>
            <DocLink route="addUser" text="Learn more about managing Members" />
          </>
        ),
        key: uuid(),
      },
    ],
  },
  instantDatasetsCheckout: {
    heading: "Check out a database",
    paragraphs: [
      {
        paragraph: (
          <>
            <Text mb={4}>Using our API you can check out a database from your dataset.</Text>
            <ExternalLink
              href="https://api.release.com/api-docs/#tag/Datasets/paths/~1datasets~1%7Bid%7D~1checkout/post"
              text="Learn how to check out a database from a Dataset"
            />
          </>
        ),
        key: uuid(),
      },
    ],
  },
  instantDatasetsCheckoutSecurity: {
    heading: "Security",
    paragraphs: [
      {
        paragraph: <SecureDatasetsContent />,
        key: uuid(),
      },
    ],
  },
  createNodeGroup: {
    heading: "Creating A Node Group",
    paragraphs: [
      {
        paragraph: (
          <>
            <Text mb={4}>
              Node groups represent the type and number of pods you want running within your cluster.
            </Text>
            <DocLink route="createNodeGroup" text="Learn more about creating a node group" />
          </>
        ),
        key: uuid(),
      },
    ],
    key: "createNodeGroup",
  },
  appEnvironments: {
    heading: "Environments",
    paragraphs: [
      {
        paragraph: <Text>These are the environments where your app has been deployed.</Text>,
        key: uuid(),
      },
    ],
    key: "appEnvironments",
  },
  defaultDependencySchedule: {
    heading: "Default Dependency Schedule",
    paragraphs: [
      {
        key: uuid(),
        paragraph: (
          <Text>
            Set the default pause and resume schedule for all of your non-production environments in this
            application.
          </Text>
        ),
      },
    ],
  },
};

export type LearnMoreEntryKey = keyof typeof learnMore;
